import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { take, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { DataService } from "src/app/services/Data.service";
import { DcpService } from "src/app/services/dcp/dcp.service";
import { DcpShortNames } from "../../../enums/dcp-short-names.enum";
import { ComboData, SearchItemsRequest } from "../../models/dcp-modal";
import { SlottedModalService } from "../../services/slotted-modal.service";
import { UnitSystem } from "src/app/shared/enums/unit-system.enum";

@Component({
  selector: "app-dcp-filter",
  templateUrl: "./dcp-filter.component.html",
  styleUrls: ["./dcp-filter.component.css"],
})
export class DcpFilterComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() dcpService: DcpService;
  @Output() filtersChanged = new EventEmitter<void>();

  subfiltersVisible: boolean = false;
  isLoadingFilters: boolean = false;

  isInitialized: boolean = false;

  fgFilters: UntypedFormGroup = this.fb.group({
    simulateUserRole: [false],
    fgMainFilters: this.fb.group({
      outsideDiameter: [null, Validators.required],
      wallThickness: [null, Validators.required],
    }),
    fgSubFilters: this.fb.group({
      grade: [null],
      connection: [null],
      minWallThickness: [null],
      type: [null],
      pipeBodyDrift: [null],
      specialBevel: [null],
      connectionODOption: [null],
    }),
  });

  filters: { [key in DcpShortNames]: ComboData[] };

  canSimulateUserRole: boolean = false;

  unitSystem = UnitSystem.USC;

  constructor(
    private fb: UntypedFormBuilder,
    private slottedModalService: SlottedModalService,
    public dataService: DataService
  ) {}

  ngOnInit(): void {
    this.dataService.currentRolSource
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        this.canSimulateUserRole =
          role.some((x) => x == "ADMIN") || role.some((x) => x == "SUPER_USER");
      });

    this.fgFilters
      .get("simulateUserRole")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fgFilters.get("fgMainFilters").reset();
      });

    // Don't auto-load filters - let preloadFilters handle it or load basic filters if needed
  }

  private resetMainFilters() {
    // Clear main filters (OD and WT) when modal opens
    this.fgFilters.get("fgMainFilters").reset({ emitEvent: false });
    this.fgFilters.get("fgSubFilters").reset({ emitEvent: false });
    this.subfiltersVisible = false;
  }

  private resetSubFilters() {
    // Clear sub filters
    this.fgFilters.get("fgSubFilters").reset({ emitEvent: false });
  }

  ngOnDestroy(): void {
    // Clear any pending timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  updateSimulateUserRole(value: boolean) {
    this.fgFilters.get("simulateUserRole").setValue(value);
  }

  changeMainFilters(mainFiltersValid: boolean) {

    if(mainFiltersValid === false) {
      this.resetSubFilters();
    }

    // Use unified flow instead of separate logic
    this.updateFilters();
  }

  // Add debounce property to prevent multiple simultaneous calls
  private updateTimeout: any = null;
  private isUpdating: boolean = false;

  onOutsideDiameterChanged(outsideDiameter: string) {
    this.debouncedUpdateFilters();
  }

  onWallThicknessChanged(wallThickness: string) {
    this.debouncedUpdateFilters();
  }

  private debouncedUpdateFilters() {
    // Clear any existing timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    // Prevent multiple simultaneous updates
    if (this.isUpdating) {
      return;
    }

    // Set a small timeout to debounce rapid changes
    this.updateTimeout = setTimeout(() => {
      this.updateFilters();
      this.updateTimeout = null;
    }, 100); // 100ms debounce
  }

  private updateFilters() {
    // Prevent multiple simultaneous updates
    if (this.isUpdating) {
      return;
    }

    const outsideDiameter = this.fgFilters
      .get("fgMainFilters")
      .get("outsideDiameter").value;
    const wallThickness = this.fgFilters
      .get("fgMainFilters")
      .get("wallThickness").value;

    // If both filters are cleared, reload basic filters
    if (!outsideDiameter && !wallThickness) {
      this.loadBasicFilters();
      return;
    }

    const mainFiltersValid = outsideDiameter && wallThickness;

    this.isUpdating = true;
    this.isLoadingFilters = true;
    this.filtersChanged.emit();

    // Single unified request - size determines behavior
    const request = this.getSearchItemsRequest(mainFiltersValid);

    this.dcpService.getProductsByFilter(request).subscribe(
      (searchItemsResponse) => {
        this.filters = searchItemsResponse.filters;

        // If size > 0, load products in table
        if (mainFiltersValid && searchItemsResponse.datasheets) {
          this.slottedModalService.setDcpProducts(
            searchItemsResponse.datasheets
          );
          this.subfiltersVisible = true;
        } else {
          // Clear table when size is 0 (only one filter selected)
          this.slottedModalService.setDcpProducts([]);
          this.subfiltersVisible = false;
        }

        this.isLoadingFilters = false;
        this.isUpdating = false;
      },
      (error) => {
        this.isLoadingFilters = false;
        this.isUpdating = false;
        console.error("Error updating filters:", error);
      }
    );
  }

  // Unified method that handles all filter changes with single request

  private getSearchItemsRequest(
    loadProducts: boolean = false
  ): SearchItemsRequest {
    let filterNames: DcpShortNames[] = [];
    let page: number = 0;
    let size: number;
    let simulateUserRole: boolean;

    if (loadProducts) {
      // Both filters selected - load products and all filters (size: 1000)
      size = 1000;
      filterNames = [
        DcpShortNames.OutsideDiameter,
        DcpShortNames.WallThickness,
        DcpShortNames.Grade,
        DcpShortNames.Connection,
        DcpShortNames.MinWallThickness,
        DcpShortNames.Type,
        DcpShortNames.PipeBodyDrift,
        DcpShortNames.SpecialBevel,
        DcpShortNames.ConnectionOdOption,
      ];
    } else {
      // Only one filter - update options only (size: 0)
      size = 0;
      filterNames = [
        DcpShortNames.OutsideDiameter,
        DcpShortNames.WallThickness,
      ];
    }

    simulateUserRole = this.fgFilters.get("simulateUserRole").value;

    let searchItemsRequest: SearchItemsRequest = {
      page: page,
      size: size,
      filter: {
        filtersNames: filterNames,
        unitChanged: false,
        unitSystem: this.unitSystem,
      },
      simulateUserRole: simulateUserRole,
    };

    // Add current filter values
    if (this.fgFilters.get("fgMainFilters").get("outsideDiameter").value) {
      searchItemsRequest.filter.outsideDiameter = Number(
        this.fgFilters.get("fgMainFilters").get("outsideDiameter").value
      );
    }

    if (this.fgFilters.get("fgMainFilters").get("wallThickness").value) {
      searchItemsRequest.filter.wallThickness = this.fgFilters
        .get("fgMainFilters")
        .get("wallThickness").value;
    }

    // Add sub-filters if loading products
    if (loadProducts) {
      if (this.fgFilters.get("fgSubFilters").get("grade").value) {
        searchItemsRequest.filter.grade = this.fgFilters
          .get("fgSubFilters")
          .get("grade").value;
      }

      if (this.fgFilters.get("fgSubFilters").get("connection").value) {
        searchItemsRequest.filter.connection = this.fgFilters
          .get("fgSubFilters")
          .get("connection").value;
      }

      if (this.fgFilters.get("fgSubFilters").get("minWallThickness").value) {
        searchItemsRequest.filter.wtTolerance = Number(
          this.fgFilters.get("fgSubFilters").get("minWallThickness").value
        );
      }

      if (this.fgFilters.get("fgSubFilters").get("type").value) {
        searchItemsRequest.filter.type = this.fgFilters
          .get("fgSubFilters")
          .get("type").value;
      }

      if (this.fgFilters.get("fgSubFilters").get("pipeBodyDrift").value) {
        searchItemsRequest.filter.pipeBodyDrift = String(
          this.fgFilters.get("fgSubFilters").get("pipeBodyDrift").value
        );
      }

      if (this.fgFilters.get("fgSubFilters").get("specialBevel").value) {
        searchItemsRequest.filter.specialBevel = this.fgFilters
          .get("fgSubFilters")
          .get("specialBevel").value;
      }

      if (this.fgFilters.get("fgSubFilters").get("connectionODOption").value) {
        searchItemsRequest.filter.connectionOdOption = this.fgFilters
          .get("fgSubFilters")
          .get("connectionODOption").value;
      }
    }

    return searchItemsRequest;
  }

  private getUnifiedSearchItemsRequest(filterValues: any): SearchItemsRequest {
    let filterNames: DcpShortNames[] = [];
    let page: number = 0;
    let size: number;
    let simulateUserRole: boolean;

    // Check both filterValues parameter AND current form values to determine if we should load products
    const hasODFromValues = filterValues.outsideDiameter;
    const hasWTFromValues = filterValues.wallThickness;
    const hasODFromForm = this.fgFilters
      .get("fgMainFilters")
      .get("outsideDiameter").value;
    const hasWTFromForm = this.fgFilters
      .get("fgMainFilters")
      .get("wallThickness").value;

    const shouldLoadProducts =
      (hasODFromValues && hasWTFromValues) || (hasODFromForm && hasWTFromForm);

    if (shouldLoadProducts) {
      size = 1000;
      filterNames = [
        DcpShortNames.OutsideDiameter,
        DcpShortNames.WallThickness,
        DcpShortNames.Grade,
        DcpShortNames.Connection,
        DcpShortNames.MinWallThickness,
        DcpShortNames.Type,
        DcpShortNames.PipeBodyDrift,
        DcpShortNames.SpecialBevel,
        DcpShortNames.ConnectionOdOption,
      ];
    } else {
      size = 0;
      filterNames = [
        DcpShortNames.OutsideDiameter,
        DcpShortNames.WallThickness,
      ];
    }

    simulateUserRole = this.fgFilters.get("simulateUserRole").value;

    let searchItemsRequest: SearchItemsRequest = {
      page: page,
      size: size,
      filter: {
        filtersNames: filterNames,
        unitChanged: false,
        unitSystem: this.unitSystem,
      },
      simulateUserRole: simulateUserRole,
    };

    // Use form values if they exist, otherwise use filterValues
    const finalOD = hasODFromForm || hasODFromValues;
    const finalWT = hasWTFromForm || hasWTFromValues;

    searchItemsRequest.filter.outsideDiameter = finalOD
      ? Number(finalOD)
      : null;
    searchItemsRequest.filter.wallThickness =
      finalWT && filterValues.nominalWeight
        ? `${finalWT}#(${filterValues.nominalWeight})`
        : finalWT;

    if (shouldLoadProducts) {
      searchItemsRequest.filter.grade = filterValues.grade;
      searchItemsRequest.filter.connection = filterValues.connection;
      searchItemsRequest.filter.wtTolerance = filterValues.minWallThickness
        ? (filterValues.minWallThickness - 100) / 100
        : null;
    }

    return searchItemsRequest;
  }

  resetFilters(isResetBtnEvent: boolean = false) {
    if (!this.isInitialized || isResetBtnEvent) {
      this.subfiltersVisible = false;
      this.isLoadingFilters = false;
      this.fgFilters.get("fgMainFilters").reset({}, { emitEvent: false });
      this.fgFilters.get("fgSubFilters").reset({ emitEvent: false });
      this.slottedModalService.setDcpProducts([]);
      this.isInitialized = true;

      // Reload basic filters
      this.loadBasicFilters();
    }
  }

  // Method to clear filters when opening modal fresh (can be called from parent)
  clearFiltersForNewSearch() {
    this.resetMainFilters();
    this.loadBasicFilters();
  }

  private loadBasicFilters() {
    // Don't reload if filters already have values and we're not explicitly resetting
    const hasOD = this.fgFilters
      .get("fgMainFilters")
      .get("outsideDiameter").value;
    const hasWT = this.fgFilters
      .get("fgMainFilters")
      .get("wallThickness").value;

    if (hasOD || hasWT) {
      return;
    }

    this.isLoadingFilters = true;

    const basicRequest: SearchItemsRequest = {
      page: 0,
      size: 0, // Only load filter options
      filter: {
        filtersNames: [
          DcpShortNames.OutsideDiameter,
          DcpShortNames.WallThickness,
        ],
        unitChanged: false,
        unitSystem: this.unitSystem,
      },
      simulateUserRole: this.fgFilters.get("simulateUserRole").value,
    };

    this.dcpService.getProductsByFilter(basicRequest).subscribe(
      (searchItemsResponse) => {
        this.filters = searchItemsResponse.filters;
        this.slottedModalService.setDcpProducts([]);
        this.subfiltersVisible = false;
        this.isLoadingFilters = false;
      },
      (error) => {
        this.isLoadingFilters = false;
      }
    );
  }

  preloadFilters(filterValues: any, unitSystem: UnitSystem, reset: boolean) {
    const hasAnyValue = Object.values(filterValues).some(
      (v) => v !== null && v !== undefined && v !== "" && v !== false
    );

    if (!hasAnyValue && !reset) {
      // Only load basic filters if we don't already have filters loaded
      if (!this.filters) {
        this.loadBasicFilters();
      }
      return;
    }

    this.unitSystem = unitSystem;

    // If reset is true or we have values, process the request
    let filter = this.getUnifiedSearchItemsRequest(filterValues);

    this.dcpService
      .getProductsByFilter(filter)
      .subscribe((searchItemsResponse) => {
        this.filters = searchItemsResponse.filters;

        if (
          searchItemsResponse.filters?.OD.length === 0 ||
          searchItemsResponse.filters?.WT.length === 0
        ) {
          this.fgFilters.get("fgMainFilters").reset();
        } else {
          // Reset sub-filters
          this.fgFilters
            .get("fgSubFilters")
            .get("grade")
            .setValue(null, { emitEvent: false });
          this.fgFilters
            .get("fgSubFilters")
            .get("connection")
            .setValue(null, { emitEvent: false });
          this.fgFilters
            .get("fgSubFilters")
            .get("type")
            .setValue(null, { emitEvent: false });
          this.fgFilters
            .get("fgSubFilters")
            .get("pipeBodyDrift")
            .setValue(null, { emitEvent: false });
          this.fgFilters
            .get("fgSubFilters")
            .get("specialBevel")
            .setValue(null, { emitEvent: false });
          this.fgFilters
            .get("fgSubFilters")
            .get("connectionODOption")
            .setValue(null, { emitEvent: false });

          // Set main filter values
          const odFilter = searchItemsResponse.filters?.OD?.find(
            (x) => x.id == filterValues.outsideDiameter
          );
          if (odFilter) {
            this.fgFilters
              .get("fgMainFilters")
              .get("outsideDiameter")
              .setValue(odFilter.id, { emitEvent: false });
          }

          const wtFilter = searchItemsResponse.filters?.WT?.find(
            (x) => x.text.split(" (")[0] == filterValues.wallThickness
          );
          if (wtFilter) {
            this.fgFilters
              .get("fgMainFilters")
              .get("wallThickness")
              .setValue(wtFilter.id, { emitEvent: false });
          }

          // Set sub-filter values if both main filters are present
          if (filterValues.outsideDiameter && filterValues.wallThickness) {
            const gradeFilter = searchItemsResponse.filters?.GR?.find(
              (x) =>
                x.id == filterValues.grade ||
                x.text == filterValues.grade ||
                x.id.toString() == filterValues.grade?.toString()
            );
            if (gradeFilter) {
              this.fgFilters
                .get("fgSubFilters")
                .get("grade")
                .setValue(gradeFilter.id, { emitEvent: false });
            }

            const connectionFilter = searchItemsResponse.filters?.CN?.find(
              (x) => x.id == filterValues.connection
            );
            if (connectionFilter) {
              this.fgFilters
                .get("fgSubFilters")
                .get("connection")
                .setValue(connectionFilter.id, { emitEvent: false });
            }

            this.fgFilters
              .get("fgSubFilters")
              .get("minWallThickness")
              .setValue(
                filterValues.minWallThickness
                  ? ((filterValues.minWallThickness - 100) / 100).toFixed(3)
                  : null,
                { emitEvent: false }
              );
          }
        }

        this.slottedModalService.setDcpProducts(searchItemsResponse.datasheets);

        // Force validation check after setting values and ensure table is shown
        const mainFiltersValid = this.fgFilters.get("fgMainFilters").valid;
        const hasOD = this.fgFilters
          .get("fgMainFilters")
          .get("outsideDiameter").value;
        const hasWT = this.fgFilters
          .get("fgMainFilters")
          .get("wallThickness").value;

        if (mainFiltersValid && hasOD && hasWT) {
          this.subfiltersVisible = true;
        } else {
          this.subfiltersVisible = false;
        }

        // Unified request already handled the appropriate size and data loading
      });
  }

  changeSubFilters($event?: any) {
    // Use unified flow to handle sub-filter changes
    this.updateFilters();
  }
}
